import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Theme,
  Typography,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import { IVacationNfaBuildStatusResponse } from "@vmosolutions/oc-interfaces";
import moment from "moment";

import { VACATION_BID_TEST_ID } from "../testIds";
import {
  crewNeedingAttention,
  isNfaBuildRunning,
  isNfaBuildStopped,
  nfaBuildProgress,
  nfaBuildStalledLine,
  nfaBuildStatusLabel,
  nfaBuildSummaryLines,
} from "./nfaBuildHelpers";

export interface INfaBuildResultsDialogProps {
  open: boolean;
  status: IVacationNfaBuildStatusResponse | null;
  loading: boolean;
  starting: boolean;
  onRerun: () => void;
  onClose: () => void;
}

const NfaBuildResultsDialogStyles = (theme: Theme): any => ({
  paper: {
    backgroundColor: theme.palette.background.paper,
    minWidth: 540,
    maxWidth: 720,
    "& .MuiDialogTitle-root": {
      ...theme.typography.subtitle2,
      fontSize: 16,
      padding: "12px 16px",
    },
    "& .MuiDialogContent-root": {
      padding: "0 16px 12px",
    },
    "& .nfa_build_status": {
      color: theme.custom.forms.labels,
      marginBottom: "8px",
    },
    "& .nfa_build_progress": {
      margin: "4px 0 10px",
    },
    "& .nfa_build_line": {
      display: "block",
    },
    "& .nfa_build_alert": {
      alignItems: "center",
      margin: "4px 0 10px",
    },
    "& .nfa_build_warning": {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      marginTop: "12px",
      color: theme.palette.secondary.main,
    },
    "& .nfa_build_problems": {
      maxHeight: 260,
      overflowY: "auto",
      marginTop: "4px",
      paddingLeft: "18px",
      "& li": {
        ...theme.typography.caption,
        color: theme.custom.forms.labels,
      },
      "& ul": {
        paddingLeft: "16px",
      },
    },
  },
});

/**
 * How the package's NFA build went: progress while it runs, then what it did and every crew member
 * who needs a look. The outcomes are stored, so this shows the last build whenever it is opened.
 */
export const NfaBuildResultsDialog = (props: INfaBuildResultsDialogProps): JSX.Element => {
  const { open, status, loading, starting, onRerun, onClose } = props;
  // The last build stays on screen until the new one is returned, so it is hidden while one starts.
  const build = starting ? null : (status?.build ?? null);
  const running = isNfaBuildRunning(build);
  const stopped = isNfaBuildStopped(build);
  // Re-read on every poll, so it appears once the build has sat still for a while.
  const stalled = build ? nfaBuildStalledLine(build, new Date()) : null;
  const problems = crewNeedingAttention(status?.crew ?? []);

  // A stopped build offers it beside why it stopped, in red, rather than at the foot of the dialog.
  const rerunButton = (
    <Button
      size="small"
      variant="contained"
      color={stopped ? "error" : "primary"}
      disabled={!status?.buildAllowed || starting}
      onClick={onRerun}
      data-testid={VACATION_BID_TEST_ID.nfaBuildRerunButton}
    >
      Run Again
    </Button>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      data-testid={VACATION_BID_TEST_ID.nfaBuildDialog}
      PaperProps={{ sx: (theme: Theme) => NfaBuildResultsDialogStyles(theme).paper }}
    >
      <DialogTitle>NFA Build</DialogTitle>
      <DialogContent>
        {!build ? (
          <Typography variant="body2" className="nfa_build_status">
            {starting ? "Starting..." : loading ? "Loading..." : "NFAs have not been built for this package yet."}
          </Typography>
        ) : (
          <>
            <Typography variant="body2" className="nfa_build_status">
              {`${nfaBuildStatusLabel(build)} - started ${moment(build.startedAt).format("MMM D, YYYY HH:mm")}`}
            </Typography>

            {stopped && (
              <Alert
                severity="error"
                className="nfa_build_alert"
                action={rerunButton}
                data-testid={VACATION_BID_TEST_ID.nfaBuildStoppedAlert}
              >
                {build.error ?? "The build stopped before it finished. Run it again to carry on."}
              </Alert>
            )}

            {running && (
              <LinearProgress
                className="nfa_build_progress"
                variant="determinate"
                value={nfaBuildProgress(build)}
                data-testid={VACATION_BID_TEST_ID.nfaBuildProgress}
              />
            )}

            {!!stalled && (
              <Alert severity="warning" className="nfa_build_alert" data-testid={VACATION_BID_TEST_ID.nfaBuildStalled}>
                {stalled}
              </Alert>
            )}

            {nfaBuildSummaryLines(build).map((line) => (
              <Typography key={line} variant="body2" className="nfa_build_line">
                {line}
              </Typography>
            ))}

            {!!problems.length && (
              <>
                <Typography variant="body2" className="nfa_build_warning">
                  <WarningIcon fontSize="small" />
                  {`${problems.length} crew member${problems.length > 1 ? "s need" : " needs"} a look`}
                </Typography>
                <ul className="nfa_build_problems" data-testid={VACATION_BID_TEST_ID.nfaBuildProblems}>
                  {problems.map((problem) => (
                    <li key={problem.userId}>
                      {`${problem.name}${problem.employeeId ? ` (${problem.employeeId})` : ""}: ${problem.summary}`}
                      {!!problem.conflicts.length && (
                        <ul>
                          {problem.conflicts.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}

        {!running && !!status?.buildRefusedReason && (
          <Typography variant="body2" className="nfa_build_warning">
            <WarningIcon fontSize="small" />
            {status.buildRefusedReason}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          size="small"
          variant="contained"
          color="secondary"
          onClick={onClose}
          data-testid={VACATION_BID_TEST_ID.nfaBuildCloseButton}
        >
          Close
        </Button>
        {!!build && !running && !stopped && rerunButton}
      </DialogActions>
    </Dialog>
  );
};

export default NfaBuildResultsDialog;
